/**
 * Drafts API service for saving, listing, restoring, and deleting Create page drafts.
 */

import type { DraftSummary, DraftDetail } from '@/lib/types';
import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface SaveDraftRequest {
  title: string;
  inputMethod: string;
  languageCode: string;
  items: {
    audios: Array<{ filename: string; contentType: string; duration: number }>;
    photos: Array<{ filename: string; contentType: string; timestamp: number }>;
    notes: Array<{ id: string; text: string; timestamp: number }>;
    streamingTranscripts: Array<{ transcript: string; duration: number }>;
  };
}

export interface SaveDraftResponse {
  draftId: string;
  uploadUrls: {
    audios?: string[];
    photos?: string[];
  };
}

class DraftsApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'DraftsApi');
  }

  async saveDraft(request: SaveDraftRequest): Promise<SaveDraftResponse> {
    this.log.debug('Saving draft', { action: 'saveDraft', inputMethod: request.inputMethod });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/drafts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to save draft', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to save draft');
    }

    this.log.info('Draft saved', { action: 'saveDraft' });
    return response.json();
  }

  async listDrafts(): Promise<DraftSummary[]> {
    this.log.debug('Listing drafts', { action: 'listDrafts' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/drafts`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list drafts', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list drafts');
    }

    const result = await response.json();
    return result.drafts;
  }

  async getDraft(draftId: string): Promise<DraftDetail> {
    this.log.debug('Getting draft', { action: 'getDraft', draftId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/drafts/${draftId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get draft', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get draft');
    }

    return response.json();
  }

  async deleteDraft(draftId: string): Promise<void> {
    this.log.debug('Deleting draft', { action: 'deleteDraft', draftId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/drafts/${draftId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete draft', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete draft');
    }

    this.log.info('Draft deleted', { action: 'deleteDraft', draftId });
  }

  /**
   * Upload a blob to S3 using a presigned URL with progress tracking.
   */
  async uploadBlob(
    presignedUrl: string,
    blob: Blob,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(blob);
    });
  }
}

// Export singleton instance
export const draftsApi = new DraftsApiService();

// Export class for testing or custom instances
export { DraftsApiService };
